import { holdNamed, holdersOf, isReached, listAbove, openTasks, phasesOf, stepCalled, tasksWaiting, type ProcessStepDto } from '@nsb/core';
import type { ProcessModel } from './useProcess.ts';

/**
 * What a step's panel says holds it back and what it waits on to be done, read by the rules the API refuses by (design
 * study 24, Jason's answers of 2026-10-08): every gate and inspection not passed holds everything after it, however far
 * down (L-17 B, core `gateHolds`); an inspection in a step's list waits on what is listed above it (L-25d, core
 * `listAbove`); a step is done only once every task in it is, the client's counted as theirs (L-23, core `openTasks`).
 * What holds each step is read once, by the model (`ProcessModel.held`), the one source a tile, a row and a panel read.
 */

/** The phase a gate of the tree closes, by the tree's name for it; none for any other step. */
const closes = (model: ProcessModel, s: Pick<ProcessStepDto, 'key' | 'instance'>): string | null =>
  s.instance === null && s.key ? (phasesOf(model.dto.tree).find((ph) => ph.closedBy !== null && ph.closedBy === s.key)?.name ?? null) : null;

/**
 * A step of the client's the work has reached, as their Waiting on you reads it (core `reachedFor`, the API's): its lines
 * done, nothing before it holding it (L-17 B), not come late or inside a step come late and not confirmed, and for a gate
 * the rest of the phase it closes done, its estimate set where it delivers one, and its own tasks done (L-23).
 */
function reachedForClient(model: ProcessModel, s: ProcessStepDto): boolean {
  // Closed to them, cancelled or archived (core `clientClosed`): nothing waits on them, as their home reads it.
  if (model.dto.clientClosed) return false;
  if (s.state === 'done' || !isReached(model.process, s.id, model.phases) || (model.held.get(s.id)?.length ?? 0) > 0) return false;
  const late = [s.id, ...holdersOf(model.process, s.id)].some((id) => {
    const d = model.step(id)?.deviation;
    return d?.kind === 'late' && d.answer !== 'confirmed';
  });
  if (late) return false;
  if (s.estimate.delivers && !s.estimate.history.length) return false;
  if (s.kind !== 'gate') return true;
  const phase = (model.holds.get(s.id) ?? []).some((id) => model.step(id)?.state !== 'done');
  return !phase && openTasks(model.process, s.id).length === 0;
}

/**
 * Why a step not done is held, as design study 24 drew it (L-17 B): "Construction Authorized, two steps back, is not done
 * yet: it waits on the client." and the way past it: "A gate holds everything after it, however far down. Stakeout goes
 * under way once Construction Authorized is done, or once it is marked done early with a reason the client confirms."
 * Null when nothing holds it.
 */
export function heldWords(model: ProcessModel, s: ProcessStepDto): { hold: ProcessStepDto; line: string; way: string } | null {
  // Done, or on an archived job kept as it ended (L-19 A), nothing holds it back any more.
  if (s.state === 'done' || model.dto.kept === 'whole') return null;
  const [h, ...rest] = model.held.get(s.id) ?? [];
  const hold = h ? model.step(h.id) : undefined;
  if (!h || !hold) return null;
  // Named with the step it is in, for a task in a list ("Inspection in Footings and Foundation"), as the API names it.
  const name = stepCalled(model.process, hold.id, undefined, s.id);
  const closer = closes(model, hold);
  const own = model.dto.kind === 'internal';
  // Said to wait on the client only once it is theirs to approve: held itself, it waits on what holds it.
  const waiting =
    hold.kind === 'gate'
      ? hold.deviation?.kind === 'early_gate' && hold.deviation.answer === 'waiting' && (model.holds.get(hold.id) ?? []).some((id) => model.step(id)?.state !== 'done')
        ? ': marked done early, it waits on the client to confirm it'
        : hold.owner === 'client' && !own && reachedForClient(model, hold)
          ? ': it waits on the client'
          : ''
      : '';
  const more = rest.length ? ` ${rest.length === 1 ? 'One more' : `${rest.length} more`} further back ${rest.length === 1 ? 'holds' : 'hold'} it too.` : '';
  const what = stepCalled(model.process, s.id);
  const goes = s.kind === 'gate' ? 'can be marked done' : s.kind === 'inspection' ? 'can be recorded' : s.kind === 'milestone' ? 'can be reached' : s.kind === 'decision' ? 'can be decided' : 'goes under way';
  const way =
    hold.kind === 'inspection'
      ? `An inspection holds everything after it, however far down. ${what} ${goes} once ${name} passes, or is recorded as passed on an override with a reason.`
      : `A gate holds everything after it, however far down. ${what} ${goes} once ${name} is done, or once it is marked done early with a reason${closer && !own ? ' the client confirms' : ''}.`;
  return { hold, line: `${holdNamed({ kind: hold.kind, title: name }, h.back, closer)}${waiting}.${more}`, way };
}

/**
 * What a step waits on of its own tasks to be done, in the words the API refuses with (L-23): "Waiting on the client:
 * 2 of their tasks. Build Contract is done once they are." Null when every task in it is done.
 */
export function tasksWords(model: ProcessModel, s: ProcessStepDto): string | null {
  if (s.state === 'done') return null;
  const open = openTasks(model.process, s.id).map((t) => ({ title: t.title, client: model.step(t.id)?.owner === 'client' }));
  return open.length ? tasksWaiting(s.title, open) : null;
}

/** An inspection in a step's list: what is listed above it and not done, which a pass waits on (L-25d). */
export function listedAboveOpen(model: ProcessModel, s: ProcessStepDto): ProcessStepDto[] {
  return listAbove(model.process, s.id)
    .map((x) => model.step(x.id))
    .filter((x): x is ProcessStepDto => Boolean(x && x.state !== 'done'));
}

/** A task of the client's in NSB's step (L-23): theirs to do, NSB marking it for them on Decisions. */
export const isClientTask = (s: ProcessStepDto): boolean => s.owner === 'client' && s.parentId !== null && (s.kind === 'work' || s.kind === 'decision');

/** Where a task of the client's stands for them: on their Waiting on you once the work reaches it, else coming up. */
export function clientTaskStanding(model: ProcessModel, s: ProcessStepDto): string {
  if (s.state === 'done') return 'The client’s · done';
  // Closed to them, cancelled or archived (L-19 A, core `clientClosed`): nothing waits on them or comes up, as their home reads it.
  if (model.dto.kept === 'whole' || model.dto.clientClosed) return 'The client’s · not done';
  return reachedForClient(model, s) ? 'The client’s · on their Waiting on you' : 'The client’s · coming up for them';
}

/** The full stop after a quoted reason or note, unless the words already end a sentence of their own. */
export const fullStop = (r: string | null | undefined): string => (/[.!?…]$/.test((r ?? '').trim()) ? '' : '.');
